import * as React from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import { gql, useQuery } from '@apollo/client';
import {
	Stack,
	Dialog,
	DialogTitle,
	DialogContent,
	Typography,
	DialogActions,
	Button,
	Skeleton,
	Alert,
	AlertTitle,
	Snackbar,
	Fade,
	Chip
} from '@mui/material';
import { SelectComponent, SelectOption } from '../select';
import { useMemo, useState, useEffect, useRef } from 'react';
import { useAuthContext } from '../../contexts/auth-context';
import { useNavigate } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { logAdminUiEvent } from '../../utils/sentry.util';

interface ShopSelectorProps {
	open?: boolean;
	onClose?: () => void;
	onShopChange?: () => void;
	onError?: (error: string) => void;
	standalone?: boolean;
}

// Distinct name from the RolesTab document of the same shape: that one only
// needs ids and cities, this one also needs the business kind so it can be
// persisted on switch. Same-named documents must be identical or codegen fails.
const GET_USER_SHOP_ACCESS = gql`
query GetShopAccessForLocationSwitch {
	getUserShopAccess {
		_id
		name,
		location {
			_id
			name
			address {
				city
			}
			businessType
		}
	}
}`;

type Location = {
	_id: string;
	name?: string;
	address: {
		city: string;
	}
	businessType?: string | null;
}

type Shop = {
	_id: string;
	name: string;
	location: Location[]
}

const LoadingSkeleton = () => (
	<Stack spacing={3}>
		<Stack spacing={1}>
			<Skeleton variant="text" width={100} height={20} />
			<Skeleton variant="rectangular" height={56} />
		</Stack>
		<Stack spacing={1}>
			<Skeleton variant="text" width={100} height={20} />
			<Skeleton variant="rectangular" height={56} />
		</Stack>
	</Stack>
);

const MAX_RETRY_COUNT = 3;
const NOTIFICATION_DURATION = 1500;
const FADE_DURATION = {
	enter: 200,
	exit: 100
};

const getStoredSelectionValue = (key: string): string | undefined => {
	const value = localStorage.getItem(key);

	if (!value || value === 'null' || value === 'undefined') {
		return undefined;
	}

	return value;
};

export const ShopSelector = (props: ShopSelectorProps) => {
	const { 
		open = false, 
		onClose, 
		onShopChange, 
		onError,
		standalone = false 
	} = props;

	const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
	const [selectedLocation, setLocation] = useState<Location | null>(null);
	const [retryCount, setRetryCount] = useState(0);
	const [showSuccess, setShowSuccess] = useState(false);
	const navigationTimeoutRef = useRef<number>();
	const navigate = useNavigate();

	const { jwtDecoded, setShop } = useAuthContext();
	const { data, loading, error, refetch } = useQuery(GET_USER_SHOP_ACCESS, {
		onError: (error) => {
			console.error('Failed to fetch shops:', error);
			const errorMessage = error.networkError 
				? 'Unable to connect to the server. Please check your internet connection.'
				: error.graphQLErrors?.length > 0
					? 'Something went wrong while fetching shops. Please try again.'
					: 'An unexpected error occurred. Please try again.';
			onError?.(errorMessage);
		},
		notifyOnNetworkStatusChange: true,
	});

	useEffect(() => {
		return () => {
			if (navigationTimeoutRef.current) {
				window.clearTimeout(navigationTimeoutRef.current);
			}
		};
	}, []);

	/* Options carry ids; the full record is resolved in the change handler. */
	const shops = useMemo<SelectOption[]>(() => {
		if (data?.getUserShopAccess) {
			return data.getUserShopAccess.map((item: Shop) => ({ label: item.name, value: item._id }));
		}
		return [];
	}, [data]);

	const locations = useMemo<SelectOption[]>(() => {
		if (selectedShop?.location) {
			return selectedShop.location.map((location: Location) => ({ label: location.address.city, value: location._id }));
		}
		return [];
	}, [selectedShop]);

	const handleShopChange = (shopId: string) => {
		const shop = data?.getUserShopAccess?.find((item: Shop) => item._id === shopId) ?? null;
		setSelectedShop(shop);
		setLocation(null);
	};

	const handleLocationChange = (locationId: string) => {
		const location = selectedShop?.location?.find((item: Location) => item._id === locationId) ?? null;
		setLocation(location);
	};

	const handleSubmit = () => {
		if (selectedShop && selectedLocation) {
			const previousShopId = getStoredSelectionValue('shopId');
			const previousShopName = getStoredSelectionValue('shopName');
			const previousLocationId = getStoredSelectionValue('locationId');
			const previousLocationName = getStoredSelectionValue('locationName');
			const shopDetails = {
				shopId: selectedShop._id,
				locationId: selectedLocation._id,
				shopName: selectedShop.name,
				locationName: selectedLocation.address.city,
				businessType: selectedLocation.businessType || 'SALON'
			};

			if (!previousShopId) {
				logAdminUiEvent({
					name: 'shop.access',
					message: 'Admin UI shop selected for the first time in session',
					attributes: {
						location_id: shopDetails.locationId,
						location_name: shopDetails.locationName,
						selection_mode: standalone ? 'standalone' : 'dialog',
						shop_id: shopDetails.shopId,
						shop_name: shopDetails.shopName,
						user_id: jwtDecoded?.user_id,
					},
				});
			} else if (previousShopId !== shopDetails.shopId) {
				logAdminUiEvent({
					name: 'shop.switch',
					message: 'Admin UI shop switched',
					attributes: {
						location_id: shopDetails.locationId,
						location_name: shopDetails.locationName,
						next_shop_id: shopDetails.shopId,
						next_shop_name: shopDetails.shopName,
						previous_shop_id: previousShopId,
						previous_shop_name: previousShopName,
						selection_mode: standalone ? 'standalone' : 'dialog',
						user_id: jwtDecoded?.user_id,
					},
				});
			}

			if (!previousLocationId) {
				logAdminUiEvent({
					name: 'location.access',
					message: 'Admin UI location selected for the first time in session',
					attributes: {
						location_id: shopDetails.locationId,
						location_name: shopDetails.locationName,
						selection_mode: standalone ? 'standalone' : 'dialog',
						shop_id: shopDetails.shopId,
						shop_name: shopDetails.shopName,
						user_id: jwtDecoded?.user_id,
					},
				});
			} else if (previousLocationId !== shopDetails.locationId) {
				logAdminUiEvent({
					name: 'location.switch',
					message: 'Admin UI location switched',
					attributes: {
						next_location_id: shopDetails.locationId,
						next_location_name: shopDetails.locationName,
						previous_location_id: previousLocationId,
						previous_location_name: previousLocationName,
						selection_mode: standalone ? 'standalone' : 'dialog',
						shop_id: shopDetails.shopId,
						shop_name: shopDetails.shopName,
						user_id: jwtDecoded?.user_id,
					},
				});
			}

			localStorage.setItem('shopId', shopDetails.shopId);
			localStorage.setItem('locationId', shopDetails.locationId);
			localStorage.setItem('shopName', shopDetails.shopName);
			localStorage.setItem('locationName', shopDetails.locationName);
			localStorage.setItem('businessType', shopDetails.businessType);
			setShop(shopDetails);
			setShowSuccess(true);
			onShopChange?.();
			navigationTimeoutRef.current = window.setTimeout(() => {
				onClose?.();
				navigate('/dashboard');
			}, NOTIFICATION_DURATION);
		}
	};

	const handleCancel = () => {
		setSelectedShop(null);
		setLocation(null);
		onClose?.();
	};

	const handleRetry = () => {
		if (retryCount >= MAX_RETRY_COUNT) {
			onError?.('Maximum retry attempts reached. Please try again later.');
			return;
		}
		setRetryCount(count => count + 1);
		refetch().catch(error => {
			console.error('Retry failed:', error);
			const errorMessage = error.networkError 
				? 'Still unable to connect. Please check your connection and try again.'
				: 'Failed to retry. Please try again later.';
			onError?.(errorMessage);
		});
	};

	const getErrorMessage = (error: any) => {
		if (retryCount >= MAX_RETRY_COUNT) {
			return {
				title: 'Too Many Attempts',
				message: 'Maximum retry attempts reached. Please try again later.',
				icon: <ErrorOutlineIcon sx={{ fontSize: 40, color: 'error.main' }} />,
			};
		}

		if (error.networkError) {
			return {
				title: 'Network Error',
				message: 'Unable to connect to the server. Please check your internet connection.',
				icon: <WifiOffIcon sx={{ fontSize: 40, color: 'error.main' }} />,
			};
		}

		if (error.graphQLErrors?.length > 0) {
			return {
				title: 'Server Error',
				message: 'Something went wrong while fetching shops. Please try again.',
				icon: <ErrorOutlineIcon sx={{ fontSize: 40, color: 'error.main' }} />,
			};
		}

		return {
			title: 'Error',
			message: 'An unexpected error occurred. Please try again.',
			icon: <ErrorOutlineIcon sx={{ fontSize: 40, color: 'error.main' }} />,
		};
	};

	const renderContent = () => {
		if (loading) {
			return <LoadingSkeleton />;
		}

		if (error) {
			const errorInfo = getErrorMessage(error);
			return (
				<Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
					{errorInfo.icon}
					<Alert 
						severity="error" 
						sx={{ width: '100%' }}
					>
						<AlertTitle>{errorInfo.title}</AlertTitle>
						{errorInfo.message}
						{retryCount > 0 && (
							<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
								Attempt {retryCount} of {MAX_RETRY_COUNT}
							</Typography>
						)}
					</Alert>
					<LoadingButton
						variant="outlined"
						onClick={handleRetry}
						loading={loading}
						startIcon={<RefreshIcon />}
						loadingPosition="start"
						disabled={retryCount >= MAX_RETRY_COUNT}
					>
						{retryCount >= MAX_RETRY_COUNT ? 'Maximum Attempts Reached' : 'Retry'}
					</LoadingButton>
				</Stack>
			);
		}

		if (!data?.getUserShopAccess?.length) {
			return (
				<Alert 
					severity="info" 
					sx={{ width: '100%' }}
				>
					<AlertTitle>No Shops Available</AlertTitle>
					Please contact your administrator to get access to a shop.
				</Alert>
			);
		}

		return (
			<Stack spacing={3}>
				<Stack spacing={1}>
					<Typography variant="subtitle2" color="text.secondary">
						Select Shop
					</Typography>
					<SelectComponent
						sx={{
							p: "2px 4px",
							display: "flex",
							alignItems: "center",
							width: "100%",
						}}
						data={shops}
						selected={selectedShop?._id}
						label="Shop"
						onChange={handleShopChange}
					/>
				</Stack>

				<Stack spacing={1}>
					<Typography variant="subtitle2" color="text.secondary">
						Select Location
					</Typography>
					<SelectComponent
						sx={{
							p: "2px 4px",
							display: "flex",
							alignItems: "center",
							width: "100%",
						}}
						data={locations}
						selected={selectedLocation?._id}
						label="Location"
						onChange={handleLocationChange}
						disabled={!selectedShop}
					/>
				</Stack>

				{!open && (
					<LoadingButton
						loading={loading}
						variant="contained"
						disabled={!selectedShop || !selectedLocation}
						onClick={handleSubmit}
						fullWidth
					>
						Continue
					</LoadingButton>
				)}
			</Stack>
		);
	};

	if (standalone) {
		return renderContent();
	}

	return (
		<Dialog 
			open={open}
			onClose={handleCancel}
			PaperProps={{
				sx: { 
					width: '100%',
					maxWidth: 400,
				}
			}}
			TransitionProps={{
				onExited: () => {
					// Reset state when dialog is fully closed
					setSelectedShop(null);
					setLocation(null);
					setRetryCount(0);
				}
			}}
		>
			<DialogTitle>
				<Typography variant="h6">Switch Shop</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
					Select a shop and location to switch to
				</Typography>
			</DialogTitle>

			<DialogContent sx={{ pt: 1, pb: 3, px: 3 }}>
				{renderContent()}
			</DialogContent>

			{!error && data?.getUserShopAccess?.length > 0 && (
				<DialogActions sx={{ px: 3, pb: 3 }}>
					<Button onClick={handleCancel}>
						Cancel
					</Button>
					<LoadingButton
						loading={loading}
						variant="contained"
						disabled={!selectedShop || !selectedLocation}
						onClick={handleSubmit}
					>
						Switch
					</LoadingButton>
				</DialogActions>
			)}
		</Dialog>
	);
};
